import { useCallback, useEffect, useState } from 'react';
import {
  listCustomerServiceRecords,
  listCustomerNotes,
  type ServiceRecord,
  type CustomerNote,
} from '../../infra/customer360Repo';

export type { ServiceRecord, CustomerNote };
export type { CustomFieldDef, CustomFieldValue } from '../../infra/customer360Repo';

const NOTES_PAGE_SIZE = 5;

/**
 * Service records + notas 360.
 * Custom fields (F-17): diferidos — no se fetchean hasta haber UI de edición.
 */
export function useCustomerServiceRecords(
  customerId: string | null,
  visible: boolean,
) {
  const [serviceRecords, setServiceRecords] = useState<ServiceRecord[]>([]);
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [notesPage, setNotesPage] = useState(0);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!customerId) return;
    try {
      const [records, customerNotes] = await Promise.all([
        listCustomerServiceRecords(customerId),
        listCustomerNotes(customerId, NOTES_PAGE_SIZE, 0),
      ]);
      setServiceRecords(records);
      setNotes(customerNotes);
      setNotesPage(0);
      setNotesHasMore(customerNotes.length === NOTES_PAGE_SIZE);
    } catch (err) {
      console.warn('[useCustomerServiceRecords] Error loading', err);
      setServiceRecords([]);
      setNotes([]);
      setNotesPage(0);
      setNotesHasMore(false);
    }
  }, [customerId]);

  const loadNotesPage = useCallback(
    async (newPage: number) => {
      if (!customerId) return;
      setLoading(true);
      try {
        const customerNotes = await listCustomerNotes(customerId, NOTES_PAGE_SIZE, newPage * NOTES_PAGE_SIZE);
        setNotes(customerNotes);
        setNotesPage(newPage);
        setNotesHasMore(customerNotes.length === NOTES_PAGE_SIZE);
      } catch (err) {
        console.warn('[useCustomerServiceRecords] Error loading notes page', err);
      } finally {
        setLoading(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    if (!visible || !customerId) {
      setServiceRecords([]);
      setNotes([]);
      setNotesPage(0);
      setNotesHasMore(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void reload().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [customerId, visible, reload]);

  return {
    serviceRecords,
    notes,
    notesPage,
    notesHasMore,
    notesPageSize: NOTES_PAGE_SIZE,
    loadNotesPage,
    /** F-17 diferido: sin fetch hasta UI de custom fields. */
    customFieldDefs: [] as const,
    customFieldValues: [] as const,
    loading,
    reload,
  };
}
